import type {
  Equalize,
  EqualizeBoostingField,
  EqualizeFeatures,
  EqualizeFunctionField,
  EqualizeSearchableField,
} from '../../modules/equalize'
import type { EqualizeDomainService } from '../interfaces'
import { injectable } from 'inversify'
import { EqualizeFeatureEnum } from '../../enums'
// @ts-strict-ignore
@injectable()
export class PlatformEqualizeDomainService implements EqualizeDomainService {
  getEmptyInstance(): Equalize {
    return {
      id: undefined,
      metadata: {
        created: undefined,
        updated: undefined,
        author: undefined,
      },
      endpointId: 'search',
      criteria: {
        enabled: false,
        pivots: [],
      },
      title: undefined,
      notes: undefined,
      isDefault: false,
      features: {
        multiMatchSearch: {
          config: {
            searchableFields: [],
          },
        },
        semanticMatchSearch: {
          config: {
            searchableFields: [],
          },
        },
        boosting: {
          config: {
            boosts: [],
          },
        },
        functionScore: {
          config: {
            functions: [],
          },
        },
        sponsoredScore: {
          config: {
            functions: [],
          },
        },
      },
      defaultFeatures: {} as EqualizeFeatures,
    }
  }

  hasBoostingDefaultValues(features: EqualizeFeatures, defaultFeatures: EqualizeFeatures): boolean {
    return (
      !features.boosting.config.boosts.length ||
      defaultFeatures.boosting?.config.boosts.every((fieldDefault: EqualizeBoostingField) => {
        return features.boosting.config.boosts.some(
          (fieldFeature: EqualizeBoostingField) =>
            `${fieldDefault.field}${fieldDefault.value}` ===
              `${fieldFeature.field}${fieldFeature.value}` &&
            fieldDefault.boostFactor === fieldFeature.boostFactor,
        )
      })
    )
  }

  hasFunctionScoreDefaultValues(
    features: EqualizeFeatures,
    defaultFeatures: EqualizeFeatures,
  ): boolean {
    return (
      !features.functionScore.config.functions.length ||
      defaultFeatures.functionScore?.config.functions.every(
        (fieldDefault: EqualizeFunctionField) => {
          return features.functionScore.config.functions.some(
            (fieldFeature: EqualizeFunctionField) =>
              fieldDefault.fieldName === fieldFeature.fieldName &&
              fieldDefault.weight === fieldFeature.weight,
          )
        },
      )
    )
  }

  hasSponsoredScoreDefaultValues(
    features: EqualizeFeatures,
    defaultFeatures: EqualizeFeatures,
  ): boolean {
    return (
      !features.sponsoredScore.config.functions.length ||
      defaultFeatures.sponsoredScore?.config.functions.every(
        (fieldDefault: EqualizeFunctionField) => {
          return features.sponsoredScore.config.functions.some(
            (fieldFeature: EqualizeFunctionField) =>
              fieldDefault.fieldName === fieldFeature.fieldName &&
              fieldDefault.weight === fieldFeature.weight,
          )
        },
      )
    )
  }

  hasTextualMatchDefaultValues(
    features: EqualizeFeatures,
    defaultFeatures: EqualizeFeatures,
  ): boolean {
    return (
      !features.multiMatchSearch.config.searchableFields.length ||
      defaultFeatures.multiMatchSearch?.config.searchableFields.every(
        (fieldDefault: EqualizeSearchableField) => {
          return features.multiMatchSearch.config.searchableFields.some(
            (fieldFeature: EqualizeSearchableField) =>
              fieldDefault.name === fieldFeature.name && fieldDefault.boost === fieldFeature.boost,
          )
        },
      )
    )
  }

  hasSemanticMatchDefaultValues(
    features: EqualizeFeatures,
    defaultFeatures: EqualizeFeatures,
  ): boolean {
    return (
      !features.semanticMatchSearch.config.searchableFields.length ||
      defaultFeatures.semanticMatchSearch?.config.searchableFields.every(
        (fieldDefault: EqualizeSearchableField) => {
          return features.semanticMatchSearch.config.searchableFields.some(
            (fieldFeature: EqualizeSearchableField) =>
              fieldDefault.name === fieldFeature.name && fieldDefault.boost === fieldFeature.boost,
          )
        },
      )
    )
  }

  isEveryFeatureByDefault(features: EqualizeFeatures, defaultFeatures: EqualizeFeatures): boolean {
    return (
      this.hasTextualMatchDefaultValues(features, defaultFeatures) &&
      this.hasSemanticMatchDefaultValues(features, defaultFeatures) &&
      this.hasBoostingDefaultValues(features, defaultFeatures) &&
      this.hasFunctionScoreDefaultValues(features, defaultFeatures) &&
      this.hasSponsoredScoreDefaultValues(features, defaultFeatures)
    )
  }

  isValid(instance: Equalize): boolean {
    return (
      instance.title && !this.isEveryFeatureByDefault(instance.features, instance.defaultFeatures)
    )
  }

  mergeWithDefault(features: EqualizeFeatures, defaultFeatures: EqualizeFeatures): void {
    Object.entries(defaultFeatures).forEach(([_key, value]) => {
      const key = _key as keyof typeof defaultFeatures
      const areFeatureFieldsEmpty = !!this.getFeatureFields(key, features[key])?.length
      features[key] = areFeatureFieldsEmpty ? features[key] : this.copyObject(value)
    })
  }

  setDefaultByFeature(
    featureId: string,
    features: EqualizeFeatures,
    defaultFeatures: EqualizeFeatures,
  ): void {
    features[featureId] = this.copyObject(defaultFeatures[featureId])
  }

  copyObject<Type = any>(object: Type): Type {
    return JSON.parse(JSON.stringify(object))
  }

  getFeatureFields(featureId: string, feature?: Record<string, any>): [] | undefined {
    switch (featureId) {
      case EqualizeFeatureEnum.BOOSTING:
        return feature?.config.boosts
      case EqualizeFeatureEnum.MULTI_MATCH_SEARCH:
        return feature?.config.searchableFields
      case EqualizeFeatureEnum.SEMANTIC_MATCH_SEARCH:
        return feature?.config.searchableFields
      case EqualizeFeatureEnum.FUNCTION_SCORE:
        return feature?.config.functions
      case EqualizeFeatureEnum.SPONSORED_SCORE:
        return feature?.config.functions
      default:
    }
  }

  getFeaturesToService(
    features: EqualizeFeatures,
    defaultFeatures: EqualizeFeatures,
  ): Record<string, any> {
    const featuresToService = {}

    if (!this.hasTextualMatchDefaultValues(features, defaultFeatures)) {
      featuresToService[EqualizeFeatureEnum.MULTI_MATCH_SEARCH] = features.multiMatchSearch
    }
    if (!this.hasSemanticMatchDefaultValues(features, defaultFeatures)) {
      featuresToService[EqualizeFeatureEnum.SEMANTIC_MATCH_SEARCH] = features.semanticMatchSearch
    }
    if (!this.hasBoostingDefaultValues(features, defaultFeatures)) {
      featuresToService[EqualizeFeatureEnum.BOOSTING] = features.boosting
    }

    if (!this.hasFunctionScoreDefaultValues(features, defaultFeatures)) {
      featuresToService[EqualizeFeatureEnum.FUNCTION_SCORE] = features.functionScore
    }
    if (!this.hasSponsoredScoreDefaultValues(features, defaultFeatures)) {
      featuresToService[EqualizeFeatureEnum.SPONSORED_SCORE] = features.sponsoredScore
    }
    return featuresToService
  }
}
